import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Download, FileText, Lightbulb, Printer, ShieldCheck, Sparkles, Target, XCircle } from "lucide-react";
import PageShell from "../components/PageShell";
import ResumeUploadBox from "../components/ResumeUploadBox";
import useSeo from "../hooks/useSeo";
import { api } from "../lib/api";

const sampleResume = `Jane Candidate
Calgary, Alberta | jane@email.com | 403-555-0100 | LinkedIn: linkedin.com/in/jane

Summary
Reliable administrative professional with experience supporting office operations, customer service, scheduling, and data entry.

Experience
Administrative Assistant | ABC Services | 2021-2024
- Coordinated daily office schedules and supported managers with document preparation.
- Responded to customer inquiries by phone and email while maintaining professional service standards.
- Organized records, updated spreadsheets, and improved filing accuracy for the team.

Skills
Customer service, Microsoft Office, data entry, scheduling, communication, organization

Education
Business Administration Certificate`;

function ResultList({ title, items, icon: Icon, testId, positive = true }) {
  return (
    <div className="border border-[#E2E8F0] bg-white p-5" data-testid={testId}>
      <h3 className="flex items-center gap-2 font-heading text-xl font-black text-[#0A2540]" data-testid={`${testId}-title`}>
        <Icon className={`h-5 w-5 ${positive ? "text-emerald-600" : "text-amber-600"}`} /> {title}
      </h3>
      <ul className="mt-4 grid gap-3">
        {items.map((item, index) => (
          <li key={`${item}-${index}`} className="text-sm leading-6 text-[#475569]" data-testid={`${testId}-item-${index}`}>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function downloadFile(filename, content, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function createWordHtml(resumeText) {
  const escaped = resumeText
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br />");
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Improved Resume</title></head><body style="font-family: Arial, sans-serif; line-height: 1.5; color: #111827;">${escaped}</body></html>`;
}

export default function ResumeChecker() {
  useSeo("Free Resume Score Checker", "Use TalentBridge Consulting's free resume score checker for resume building, editing tips, strengths, issues, keywords, and bullet improvements.");
  const [resumeText, setResumeText] = useState("");
  const [targetRole, setTargetRole] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [uploadInfo, setUploadInfo] = useState(null);
  const [improveChoice, setImproveChoice] = useState(null);
  const [improving, setImproving] = useState(false);
  const [improveError, setImproveError] = useState("");
  const [improvedResult, setImprovedResult] = useState(null);
  const [editableImprovedResume, setEditableImprovedResume] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    setResult(null);
    setImproveChoice(null);
    setImproveError("");
    setImprovedResult(null);
    setEditableImprovedResume("");
    try {
      const response = await api.checkResume({ resume_text: resumeText, target_role: targetRole });
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Unable to score resume. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const applySuggestedChanges = async () => {
    setImproveChoice("yes");
    setImproving(true);
    setImproveError("");
    setImprovedResult(null);
    try {
      const response = await api.improveResume({
        resume_text: resumeText,
        target_role: targetRole,
        issues: result?.issues || [],
        improvement_tips: result?.improvement_tips || [],
        keyword_suggestions: result?.keyword_suggestions || [],
        rewritten_bullet_examples: result?.rewritten_bullet_examples || [],
      });
      setImprovedResult(response.data);
      setEditableImprovedResume(response.data.improved_resume);
    } catch (err) {
      setImproveError(err.response?.data?.detail || "Unable to apply suggested changes. Please try again.");
    } finally {
      setImproving(false);
    }
  };

  const printImprovedResume = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(createWordHtml(editableImprovedResume));
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const downloadImprovedWord = () => {
    downloadFile("improved-resume.doc", createWordHtml(editableImprovedResume), "application/msword");
  };

  const downloadImprovedText = () => {
    downloadFile("improved-resume.txt", editableImprovedResume, "text/plain;charset=utf-8");
  };

  return (
    <PageShell>
      <section className="bg-white px-5 pb-16 pt-32 md:px-8 lg:pb-24" data-testid="resume-checker-page-section">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#0066FF]" data-testid="resume-checker-eyebrow">Free resume tool</p>
            <h1 className="mt-5 font-heading text-4xl font-black leading-tight text-[#0A2540] sm:text-5xl lg:text-6xl" data-testid="resume-checker-title">
              Free Resume Score Checker
            </h1>
            <p className="mt-6 text-base leading-8 text-[#475569] md:text-lg" data-testid="resume-checker-description">
              Paste your resume to get an instant score, editing tips, keyword suggestions, and stronger bullet point examples. Your resume text is not saved.
            </p>
            <div className="mt-8 grid gap-4">
              <div className="border-l-4 border-[#0066FF] bg-[#F8FAFC] p-5" data-testid="resume-privacy-card">
                <ShieldCheck className="h-6 w-6 text-[#0066FF]" />
                <p className="mt-3 text-sm font-bold text-[#0A2540]" data-testid="resume-privacy-text">Privacy-friendly: resume text is scored instantly and not stored.</p>
              </div>
              <div className="border-l-4 border-[#0066FF] bg-[#F8FAFC] p-5" data-testid="resume-help-card">
                <Lightbulb className="h-6 w-6 text-[#0066FF]" />
                <p className="mt-3 text-sm font-bold text-[#0A2540]" data-testid="resume-help-text">Use the results to improve structure, keywords, bullet points, and employer readability.</p>
              </div>
            </div>
            <Link to="/resume-tailor" className="mt-6 inline-flex items-center gap-2 bg-[#0A2540] px-5 py-3 text-sm font-bold text-white hover:bg-[#051424]" data-testid="resume-tailor-link">
              <span className="bg-[#0066FF] px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] text-white" data-testid="resume-tailor-new-badge">New</span>
              Tailor resume to a job <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <form onSubmit={submit} className="grid gap-5 border border-[#E2E8F0] bg-[#FBFBFC] p-6 md:p-8" data-testid="resume-checker-form">
            {error && <div className="border border-red-200 bg-red-50 p-3 text-sm font-bold text-red-700" data-testid="resume-checker-error-message">{error}</div>}
            <label className="grid gap-2" data-testid="resume-target-role-field">
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#475569]" data-testid="resume-target-role-label">Target role optional</span>
              <input
                value={targetRole}
                onChange={(event) => setTargetRole(event.target.value)}
                placeholder="Example: Administrative Assistant, Class 1 Driver, Help Desk Technician"
                className="min-h-11 border border-[#CBD5E1] bg-white px-3 text-sm text-[#0B1120] focus:outline-none focus:ring-2 focus:ring-[#0066FF]"
                data-testid="resume-target-role-input"
              />
            </label>
            <label className="grid gap-2" data-testid="resume-text-field">
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#475569]" data-testid="resume-text-label">Paste resume text or upload file</span>
              <ResumeUploadBox
                testId="resume-checker"
                loading={uploading}
                setLoading={setUploading}
                setError={setError}
                onParsed={(text, meta) => {
                  setResumeText(text);
                  setUploadInfo(meta);
                }}
              />
              {uploadInfo && <p className="text-xs font-bold text-emerald-700" data-testid="resume-upload-success-text">Loaded {uploadInfo.filename} · approx. {uploadInfo.word_count} words</p>}
              <textarea
                value={resumeText}
                onChange={(event) => setResumeText(event.target.value)}
                required
                rows={16}
                placeholder="Paste your resume text here..."
                className="min-h-[360px] border border-[#CBD5E1] bg-white px-3 py-3 text-sm leading-6 text-[#0B1120] focus:outline-none focus:ring-2 focus:ring-[#0066FF]"
                data-testid="resume-text-input"
              />
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <button type="button" onClick={() => setResumeText(sampleResume)} className="border border-[#0A2540] px-5 py-3 text-sm font-bold text-[#0A2540] hover:bg-[#0A2540] hover:text-white" data-testid="resume-load-sample-button">
                Load sample resume
              </button>
              <button type="submit" disabled={loading || uploading} className="inline-flex items-center justify-center gap-2 bg-[#0066FF] px-5 py-3 text-sm font-bold text-white hover:bg-[#0052CC] disabled:opacity-60" data-testid="resume-check-submit-button">
                {loading ? "Scoring..." : "Check my resume"} <Sparkles className="h-4 w-4" />
              </button>
            </div>
          </form>
        </div>
      </section>

      {result && (
        <section className="bg-[#F8FAFC] px-5 py-16 md:px-8 lg:py-24" data-testid="resume-results-section">
          <div className="mx-auto grid max-w-7xl gap-8">
            <div className="grid gap-6 lg:grid-cols-[0.45fr_1fr]" data-testid="resume-score-summary-grid">
              <div className="border border-[#E2E8F0] bg-[#0A2540] p-8 text-white" data-testid="resume-overall-score-card">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-200" data-testid="resume-score-label">Resume score</p>
                <p className="mt-4 font-heading text-7xl font-black" data-testid="resume-overall-score-value">{result.overall_score}</p>
                <p className="mt-3 text-xl font-bold" data-testid="resume-grade-text">{result.grade}</p>
                <p className="mt-5 text-sm leading-7 text-slate-200" data-testid="resume-summary-text">{result.summary}</p>
              </div>
              <div className="grid gap-4 md:grid-cols-2" data-testid="resume-category-scores-grid">
                {result.category_scores.map((category) => (
                  <div key={category.name} className="border border-[#E2E8F0] bg-white p-5" data-testid={`resume-category-${category.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-heading text-lg font-black text-[#0A2540]" data-testid={`resume-category-title-${category.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>{category.name}</h3>
                      <span className="font-heading text-2xl font-black text-[#0066FF]" data-testid={`resume-category-score-${category.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>{category.score}</span>
                    </div>
                    <div className="mt-4 h-2 bg-[#E2E8F0]" data-testid={`resume-category-bar-${category.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                      <div className="h-2 bg-[#0066FF]" style={{ width: `${category.score}%` }} />
                    </div>
                    <p className="mt-3 text-sm leading-6 text-[#475569]" data-testid={`resume-category-feedback-${category.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>{category.feedback}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-2" data-testid="resume-feedback-grid">
              <ResultList title="Strengths" items={result.strengths} icon={CheckCircle2} testId="resume-strengths-list" />
              <ResultList title="Issues to fix" items={result.issues.length ? result.issues : ["No major issues detected. Keep tailoring your resume for each role."]} icon={XCircle} testId="resume-issues-list" positive={false} />
              <ResultList title="Improvement tips" items={result.improvement_tips} icon={Lightbulb} testId="resume-tips-list" />
              <ResultList title="Keyword suggestions" items={result.keyword_suggestions} icon={Target} testId="resume-keywords-list" />
            </div>

            <div className="border border-[#E2E8F0] bg-white p-6" data-testid="resume-bullet-examples-card">
              <h3 className="flex items-center gap-2 font-heading text-xl font-black text-[#0A2540]" data-testid="resume-bullet-examples-title"><FileText className="h-5 w-5 text-[#0066FF]" /> Rewritten bullet examples</h3>
              <div className="mt-5 grid gap-4">
                {result.rewritten_bullet_examples.map((bullet, index) => (
                  <p key={`${bullet}-${index}`} className="border-l-4 border-[#0066FF] bg-[#F8FAFC] p-4 text-sm leading-7 text-[#334155]" data-testid={`resume-bullet-example-${index}`}>{bullet}</p>
                ))}
              </div>
              <p className="mt-5 text-xs font-semibold text-[#64748B]" data-testid="resume-privacy-note-text">{result.privacy_note}</p>
            </div>

            <div className="border-2 border-[#0066FF] bg-white p-6" data-testid="resume-apply-suggestions-card">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h3 className="font-heading text-2xl font-black text-[#0A2540]" data-testid="resume-apply-suggestions-title">Want TalentBridge to apply these suggested changes?</h3>
                  <p className="mt-2 text-sm leading-6 text-[#475569]" data-testid="resume-apply-suggestions-description">Choose Yes and the app will create an editable improved resume while keeping your original format as much as possible.</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button type="button" onClick={applySuggestedChanges} disabled={improving} className="inline-flex items-center gap-2 bg-[#0066FF] px-5 py-3 text-sm font-bold text-white hover:bg-[#0052CC] disabled:opacity-60" data-testid="resume-apply-suggestions-yes-button">
                    {improving ? "Applying changes..." : "Yes, improve my resume"} <Sparkles className="h-4 w-4" />
                  </button>
                  <button type="button" onClick={() => setImproveChoice("no")} className="border border-[#CBD5E1] px-5 py-3 text-sm font-bold text-[#0A2540] hover:border-[#0066FF]" data-testid="resume-apply-suggestions-no-button">
                    No, I’ll edit myself
                  </button>
                </div>
              </div>
              {improveChoice === "no" && <p className="mt-4 border border-slate-200 bg-[#F8FAFC] p-3 text-sm font-semibold text-[#475569]" data-testid="resume-apply-suggestions-no-message">No problem — use the tips above to edit your resume manually.</p>}
              {improveError && <p className="mt-4 border border-red-200 bg-red-50 p-3 text-sm font-bold text-red-700" data-testid="resume-improve-error-message">{improveError}</p>}
            </div>

            {improvedResult && (
              <div className="border border-[#E2E8F0] bg-white p-6" data-testid="resume-improved-editor-card">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h2 className="font-heading text-2xl font-black text-[#0A2540]" data-testid="resume-improved-editor-title">Editable improved resume</h2>
                    <p className="mt-2 text-sm text-[#64748B]" data-testid="resume-improved-summary-text">{improvedResult.summary}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={printImprovedResume} className="inline-flex items-center gap-2 border border-[#CBD5E1] px-4 py-2 text-sm font-bold text-[#0A2540] hover:border-[#0066FF]" data-testid="resume-improved-print-button"><Printer className="h-4 w-4" /> Print / Save PDF</button>
                    <button type="button" onClick={downloadImprovedWord} className="inline-flex items-center gap-2 bg-[#0066FF] px-4 py-2 text-sm font-bold text-white hover:bg-[#0052CC]" data-testid="resume-improved-download-word-button"><FileText className="h-4 w-4" /> Download Word</button>
                    <button type="button" onClick={downloadImprovedText} className="inline-flex items-center gap-2 border border-[#CBD5E1] px-4 py-2 text-sm font-bold text-[#0A2540] hover:border-[#0066FF]" data-testid="resume-improved-download-text-button"><Download className="h-4 w-4" /> Download TXT</button>
                  </div>
                </div>
                <div className="mt-5 grid gap-2" data-testid="resume-improved-changes-list">
                  {improvedResult.changes_applied.map((change, index) => <p key={`${change}-${index}`} className="border-l-4 border-[#0066FF] bg-[#F8FAFC] p-3 text-sm leading-6 text-[#475569]" data-testid={`resume-improved-change-${index}`}>{change}</p>)}
                </div>
                <textarea value={editableImprovedResume} onChange={(event) => setEditableImprovedResume(event.target.value)} rows={28} className="mt-6 w-full border border-[#CBD5E1] bg-[#FBFBFC] px-4 py-4 font-mono text-sm leading-7 text-[#0B1120] focus:outline-none focus:ring-2 focus:ring-[#0066FF]" data-testid="resume-improved-editable-input" />
                <p className="mt-4 text-xs font-semibold text-[#64748B]" data-testid="resume-improved-privacy-note-text">{improvedResult.privacy_note}</p>
              </div>
            )}

            <div className="flex flex-col gap-3 bg-[#0A2540] p-6 text-white md:flex-row md:items-center md:justify-between" data-testid="resume-next-step-banner">
              <p className="font-heading text-xl font-black" data-testid="resume-next-step-text">Ready to share your improved resume with TalentBridge?</p>
              <Link to="/candidates" className="inline-flex items-center justify-center gap-2 bg-white px-5 py-3 text-sm font-bold text-[#0A2540] hover:bg-blue-50" data-testid="resume-register-candidate-link">
                Register as candidate <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}
    </PageShell>
  );
}